/**
 * Timezone-aware date utilities for the promotions cart and any other
 * surface that needs to render UTC Timestamps in a user-selected tz or
 * convert a local wall-clock entry into a UTC instant.
 *
 * Storage convention:
 *   - All datetimes persisted to Firestore are UTC Timestamps.
 *   - The user's preferred IANA tz lives at `users/{uid}.timezone`.
 *   - UI inputs are rendered in that tz; reads convert before persist.
 */

const FALLBACK_TZ = "America/Puerto_Rico"; // USVI lives in this offset (AST, no DST).

/** Best-effort detection of the browser timezone. */
export function detectBrowserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || FALLBACK_TZ;
  } catch {
    return FALLBACK_TZ;
  }
}

/** Returns the list of IANA tz identifiers if the runtime exposes it. */
export function listSupportedTimezones() {
  try {
    if (typeof Intl.supportedValuesOf === "function") {
      return Intl.supportedValuesOf("timeZone");
    }
  } catch { /* noop */ }
  return [
    "America/Puerto_Rico",
    "America/St_Thomas",
    "America/New_York",
    "America/Chicago",
    "America/Denver",
    "America/Phoenix",
    "America/Los_Angeles",
    "UTC",
  ];
}

/**
 * Compute the offset (in minutes, east of UTC) that the given IANA tz has
 * at a particular UTC instant. Necessary because IANA tzs change with DST.
 */
function tzOffsetMinutes(tz, atUtc = new Date()) {
  // Trick: format the same instant in both UTC and tz, diff the parsed parts.
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    hour12: false,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
  const parts = dtf.formatToParts(atUtc).reduce((acc, p) => {
    if (p.type !== "literal") acc[p.type] = p.value;
    return acc;
  }, {});
  // 24-hour formatter sometimes emits "24" for midnight; normalize.
  let hour = Number(parts.hour);
  if (hour === 24) hour = 0;
  const asUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    hour,
    Number(parts.minute),
    Number(parts.second)
  );
  return Math.round((asUtc - atUtc.getTime()) / 60000);
}

/**
 * Convert a local wall-clock entry (`YYYY-MM-DDTHH:mm` from a datetime-local
 * input, OR separate date+time strings) in the given tz to a UTC Date.
 */
export function toUtcFromLocal(localStr, tz) {
  if (!localStr) return null;
  const [datePart, timePart = "00:00"] = String(localStr).split("T");
  const [y, m, d] = datePart.split("-").map(Number);
  const [hh = 0, mm = 0] = timePart.split(":").map(Number);
  if (!y || !m || !d) return null;

  // Approximate the UTC instant assuming the entered wall-clock IS the UTC,
  // then correct by the tz offset at that approximate instant.
  const guess = new Date(Date.UTC(y, m - 1, d, hh, mm));
  const offset = tzOffsetMinutes(tz || FALLBACK_TZ, guess);
  return new Date(guess.getTime() - offset * 60000);
}

/**
 * Format a Date or millisecond epoch in the given tz using Intl.
 */
export function formatInUserTz(input, tz, options = {}) {
  if (!input) return "";
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat(undefined, {
    timeZone: tz || FALLBACK_TZ,
    dateStyle: "medium",
    timeStyle: "short",
    ...options,
  }).format(d);
}

/**
 * Render a Date in the given tz as a `YYYY-MM-DDTHH:mm` string suitable for
 * `<input type="datetime-local">` defaultValues.
 */
export function toLocalInputValue(input, tz) {
  if (!input) return "";
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return "";
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz || FALLBACK_TZ,
    hour12: false,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit",
  }).formatToParts(d).reduce((acc, p) => {
    if (p.type !== "literal") acc[p.type] = p.value;
    return acc;
  }, {});
  let hour = parts.hour;
  if (hour === "24") hour = "00";
  return `${parts.year}-${parts.month}-${parts.day}T${hour}:${parts.minute}`;
}

/**
 * Returns a UTC Date representing the next business day (Mon-Fri) at 00:00
 * local time in the given tz. Used as the default cart `startAt`.
 */
export function nextBusinessDayStartUtc(tz) {
  const now = new Date();
  const todayLocal = toLocalInputValue(now, tz);
  // Step forward day-by-day until we land on Mon-Fri after today.
  const [datePart] = todayLocal.split("T");
  const [y, m, d] = datePart.split("-").map(Number);
  for (let i = 1; i <= 7; i++) {
    const candidateUtc = new Date(Date.UTC(y, m - 1, d + i));
    // Determine candidate day-of-week in the user's tz.
    const wd = new Intl.DateTimeFormat("en-US", {
      timeZone: tz || FALLBACK_TZ,
      weekday: "short",
    }).format(candidateUtc);
    if (wd !== "Sat" && wd !== "Sun") {
      // Reset to 00:00 local on that candidate date.
      const localStr = `${candidateUtc.getUTCFullYear()}-${String(candidateUtc.getUTCMonth() + 1).padStart(2, "0")}-${String(candidateUtc.getUTCDate()).padStart(2, "0")}T00:00`;
      return toUtcFromLocal(localStr, tz);
    }
  }
  return new Date(Date.UTC(y, m - 1, d + 1));
}

/**
 * Number of whole days between two Dates (always >= 1 when distinct).
 */
export function daysBetween(startUtc, endUtc) {
  if (!startUtc || !endUtc) return 0;
  const s = startUtc instanceof Date ? startUtc : new Date(startUtc);
  const e = endUtc instanceof Date ? endUtc : new Date(endUtc);
  const diffMs = e.getTime() - s.getTime();
  if (!(diffMs > 0)) return 0;
  return Math.max(1, Math.ceil(diffMs / (24 * 60 * 60 * 1000)));
}

export const DEFAULT_TIMEZONE = FALLBACK_TZ;
